import { Address } from './Address'

/**
 * Project model (ID-based DOM reads)
 * - Constructor defaults keep DOM bindings stable (no nulls).
 * - fromAPI() converts DB nulls -> ''/valid objects for the UI.
 * - readFromForm() pulls current values from the DOM by element IDs.
 * - toAPI() converts '' -> null for saving to the backend.
 */
export default class Project {
    /**
     * Translation payload injected by Blade.
     * @returns {Record<string, string>}
     */
    static messages() {
        return globalThis.UploadMessages
    }

    constructor() {
        // Core fields
        this.id = 0
        this.name = ''
        this.firstname = ''
        this.lastname = ''
        this.notes = ''

        // Relations / foreign keys commonly used by your API
        this.address_id = null
        this.user_id = null

        // Embedded object for UI bindings
        this.address = new Address()

        // Future fields
        // this.photos = []
        // this.colorSamples = []
        // this.user = null
        // this.created_at = null
        // this.updated_at = null
    }

    /**
     * Create a project on the backend (first-time save).
     * @param {Object} p
     * @param {number} p.photoId
     * @param {string} p.csrfToken
     * @returns {Promise<{id:number}|undefined>}
     */
    async createProject({ photoId, csrfToken }) {
        if (!photoId) throw new Error(Project.messages().projectMissingPhotoId)

        const fd = new FormData()
        fd.append('_token', csrfToken || '')
        fd.append('photo_id', photoId)

        const response = await fetch('/projects', {
            method: 'POST',
            headers: { 'X-CSRF-TOKEN': csrfToken || '' },
            body: fd,
        })

        if (!response.ok) {
            const text = await response.text()
            throw new Error(text || Project.messages().projectCreationFailed)
        }

        const data = await response.json()
        this.id = data?.id ?? this.id
        return data
    }

    /**
     * Hydrate from API/DB payload (may contain nulls).
     * @param {Object} raw
     */
    static fromAPI(raw = {}) {
        const p = new Project()
        p.id = Number(raw.id ?? 0)
        p.name = raw.name ?? ''
        p.firstname = raw.firstname ?? ''
        p.lastname = raw.lastname ?? ''
        p.notes = raw.notes ?? ''

        p.address_id = raw.address_id ?? null
        p.user_id = raw.user_id ?? null

        p.address = Address.fromAPI(raw.address ?? {})
        return p
    }

    /**
     * Read current values from the DOM using element IDs.
     * Expects inputs with IDs: name, firstname, lastname, notes
     * Optional hidden IDs: project_id, address_id, user_id
     * Delegates address fields to Address.readFromForm()
     * @param {Document|HTMLElement} root (any container; IDs resolve globally)
     */
    readFromForm(root = document) {
        const val = (id) => (document.getElementById(id)?.value ?? '').trim()

        this.name = val('name')
        this.firstname = val('firstname')
        this.lastname = val('lastname')
        this.notes = val('notes')

        // Optional hidden inputs
        const idVal = val('project_id')
        if (idVal) this.id = Number(idVal) || this.id

        const addrIdVal = val('address_id')
        if (addrIdVal !== '') this.address_id = Number(addrIdVal) || null

        const userIdVal = val('user_id')
        if (userIdVal !== '') this.user_id = Number(userIdVal) || null

        if (!(this.address instanceof Address) || typeof this.address.readFromForm !== 'function') {
            this.address = Address.fromAPI(this.address || {})
        }

        this.address.readFromForm(root)
        return this
    }

    /**
     * Apply project fields from an imported XML project payload.
     * @param {Object} xmlProject
     * @returns {Project}
     */
    applyFromXml(xmlProject = {}) {
        if (!xmlProject || typeof xmlProject !== 'object') return this

        this.name = xmlProject.name || this.name
        this.firstname = xmlProject.firstname ?? ''
        this.lastname = xmlProject.lastname ?? ''
        this.notes = xmlProject.notes ?? ''

        if (!(this.address instanceof Address) || typeof this.address !== 'object') {
            this.address = Address.fromAPI(this.address || {})
        }

        this.address.street1 = xmlProject.locationStreet1 ?? ''
        this.address.street2 = xmlProject.locationStreet2 ?? ''
        this.address.city = xmlProject.locationCity ?? ''
        this.address.state = xmlProject.locationState ?? ''
        this.address.npa = xmlProject.locationCP ?? ''
        this.address.iso_3166_2 = xmlProject.locationCountry ?? ''

        return this
    }

    /**
     * Build API payload ('' -> null).
     */
    toAPI() {
        const nz = (v) => {
            if (v == null) return null
            const s = String(v).trim()
            return s === '' ? null : s
        }

        return {
            id: this.id || null,
            name: nz(this.name),
            firstname: nz(this.firstname),
            lastname: nz(this.lastname),
            notes: nz(this.notes),
            address_id: this.address_id ?? null,
            user_id: this.user_id ?? null,
            address: this.address ? this.address.toAPI() : new Address(),
        }
    }

    /**
     * Save the full project state for the first photo.
     * Expects already-prepared inputs.
     *
     * @param {Object} p
     * @param {number} p.photoId
     * @param {string} p.colorSampleJSON
     * @param {Blob}   p.previewBlob
     * @param {Blob}   p.maskBlob
     * @param {string} p.csrfToken
     * @returns {Promise<void>}
     */
    async saveProject({ photoId, colorSampleJSON, previewBlob, maskBlob, csrfToken }) {
        if (!photoId) throw new Error(Project.messages().projectMissingPhotoId)
        if (!previewBlob) throw new Error('Missing preview image.')
        if (!maskBlob) throw new Error('Missing mask image.')

        const fd = new FormData()
        fd.append('_token', csrfToken || '')
        fd.append('_method', 'PUT')
        fd.append('colorsample', colorSampleJSON)
        fd.append('preview', previewBlob, 'preview.jpg')
        fd.append('mask', maskBlob, 'mask.png')

        await new Promise((resolve, reject) => {
            $.ajax({
                url: `/photos/${photoId}/update`,
                type: 'POST',
                headers: { 'X-CSRF-TOKEN': csrfToken || '' },
                data: fd,
                processData: false,
                contentType: false,
                success: resolve,
                error: (jqXHR, textStatus) =>
                    reject(new Error(jqXHR?.responseText || textStatus || Project.messages().projectUploadFailed)),
            })
        })
    }

    /**
     * Save basic project information (form fields only).
     * Optionally refresh from DOM first (pass any container or document).
     *
     * @param {Object} p
     * @param {number} p.projectId
     * @param {string} p.csrfToken
     * @param {Document|HTMLElement} [p.formEl] If provided, read from DOM before saving
     * @returns {Promise<true>}
     */
    async saveProjectInfos({ projectId, csrfToken, formEl }) {
        if (!projectId) throw new Error(Project.messages().projectMissingProjectId)

        if (formEl) this.readFromForm(formEl)

        const response = await fetch(`/projects/${projectId}/update-infos`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken || '',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(this.toInfoUpdatePayload()),
        })

        if (!response.ok) {
            let message = Project.messages().projectUpdateFailed
            try {
                const payload = await response.json()
                const firstError = Object.values(payload?.errors || {})?.[0]?.[0]
                message = firstError || payload?.message || message
            } catch {
                const text = await response.text()
                if (text) message = text
            }
            throw new Error(message)
        }

        const updatedProject = await response.json()
        this.applyInfoUpdateResponse(updatedProject)

        return updatedProject
    }

    /**
     * Build the slim payload expected by projects.update_infos.
     * @returns {Object}
     */
    toInfoUpdatePayload() {
        return {
            name: this.name,
            firstname: this.firstname,
            lastname: this.lastname,
            notes: this.notes,
            address: this.address.street1,
            npa: this.address.npa,
            city: this.address.city,
            state: this.address.state,
            country_id: this.address.country_id || null,
        }
    }

    /**
     * Sync the in-memory project with the update-infos response.
     * @param {Object} payload
     * @returns {Project}
     */
    applyInfoUpdateResponse(payload) {
        this.id = Number(payload.project_id)
        this.name = payload.name
        this.firstname = payload.firstname
        this.lastname = payload.lastname
        this.notes = payload.notes

        this.address = Address.fromAPI({
            ...this.address.toAPI(),
            ...payload.address,
            country: payload.address.country_name,
        })

        return this
    }
}
